import { formatDate, isDateAfter } from 'commons/utils/date'
import { numberToDecimalString as toDecimal } from 'commons/utils/number'
import { EmptyContent, type HeadItemType, Icon, SendMessageTooltip, Table } from 'components'
import type { MetaResultRegion, MetaResultTeam } from 'services/home/interfaces'
import { getGeneralMetaItem, setGeneralMetaData } from 'store/HomeContext/homeSlice'
import { useDispatch, useSelector } from 'store/store'

import type { OfficeTypes } from '../GoalProgress'
import { officeColors } from '../GoalProgress/constants'
import * as S from './styles'

interface GeneralTableProps {
  quota: string
  selectedOffice: OfficeTypes
  screenshotComponentRef: React.MutableRefObject<null>
  isPrinting: boolean
}

const GeneralTable: React.FC<GeneralTableProps> = ({
  quota,
  selectedOffice,
  screenshotComponentRef,
  isPrinting,
}) => {
  const dispatch = useDispatch()
  const {
    loadingRanking: loading,
    generalMetaData,
    loadingTableItem,
  } = useSelector((state) => state.home)

  const getZoneUser = (item: MetaResultRegion) => {
    if (item.NomeUsuarioZona && item.EmailUsuarioZona) {
      return (
        <SendMessageTooltip
          email={item.EmailUsuarioZona.split(';')[0]}
          name={item.NomeUsuarioZona}
          phone={item.TelefoneUsuarioZona}
        >
          <span>{item.CodigoUsuarioZona ?? item.DescricaoZona}</span>
        </SendMessageTooltip>
      )
    }
    return <span>{item.DescricaoZona}</span>
  }

  const tableLabels = (): HeadItemType[] => {
    const tableHead: HeadItemType[] = [
      { columnName: 'ID', label: 'ID' },
      { columnName: 'EquipesVenda', label: 'Equipes de vendas' },
      { columnName: 'MetaTotal', label: 'Meta total' },
      { columnName: 'VolumeAprovado', label: 'Vol. aprovado' },
    ]

    if (generalMetaData.length) {
      generalMetaData[0].Dias.forEach((item) => {
        const newItem = formatDate(item.Dia, 'DD/MM')
        tableHead.push({ columnName: newItem, label: newItem })
      })
    }

    return tableHead
  }

  const tableData = () => {
    return generalMetaData.map((item) => {
      const rowContent = [
        <strong key={`title-${item.Equipe}`}>{item.Equipe}</strong>,
        <strong key={`desc-${item.Equipe}`}>{item.DescricaoEquipe}</strong>,
        <S.DataWrap key={`meta-${item.Equipe}`}>
          <Icon color={officeColors[selectedOffice]} icon="sandal" size="16" />
          <strong>{toDecimal(item.Meta)}</strong>
        </S.DataWrap>,
        <S.DataWrap achieved={item.PercentualMeta >= 100} key={`approved-${item.Equipe}`}>
          <Icon color={officeColors[selectedOffice]} icon="sandal" size="16" />
          <strong>{toDecimal(item.VolumeAprovado)}</strong>
        </S.DataWrap>,
      ]

      item.Dias.forEach((item) => {
        const isFutureDate = isDateAfter(item.Dia, new Date())
        rowContent.push(
          <S.DataWrap
            achieved={item.PercentualMeta >= 100}
            className={item.PercentualMeta >= 100 ? 'table-cell-success-bg' : undefined}
            key={`percent-${item.Dia}`}
          >
            <strong>{isFutureDate ? '-' : `${Number(item.PercentualMeta.toFixed(2))} %`}</strong>
          </S.DataWrap>,
        )
      })

      return {
        content: rowContent,
        expanded: item.expanded ?? false,
        id: item.Equipe,
        subRows: tableSubrows(item),
      }
    })
  }

  const tableSubrows = (item: MetaResultTeam) => {
    if (!item.regions?.length) return undefined

    return item.regions.map((region) => {
      const subrowContent = [
        region.Regiao,
        <S.DataWrap key={`tooltip-${region.Regiao}`}>{getZoneUser(region)}</S.DataWrap>,
        <S.DataWrap key={`meta-${region.Regiao}`}>
          <span>{toDecimal(region.Meta)}</span>
        </S.DataWrap>,
        <S.DataWrap achieved={region.PercentualMeta >= 100} key={`approved-${region.Regiao}`}>
          <span>{toDecimal(region.VolumeAprovado)}</span>
        </S.DataWrap>,
      ]

      region.Dias.forEach((dia) => {
        const isFutureDate = isDateAfter(dia.Dia, new Date())
        subrowContent.push(
          <S.DataWrap achieved={dia.PercentualMeta >= 100} key={`percent-${dia.Dia}`}>
            <span>{isFutureDate ? '-' : `${Number(dia.PercentualMeta.toFixed(2))} %`}</span>
          </S.DataWrap>,
        )
      })

      return {
        content: subrowContent,
        id: region.Regiao,
      }
    })
  }

  const setExpanded = (id: string) => {
    const selectedItem = generalMetaData.find((item) => item.Equipe === id)
    const isItemExpanded = selectedItem?.expanded
    const hasLoadedRegions = selectedItem?.regions?.length

    if (isItemExpanded || hasLoadedRegions) {
      const newMetaList = generalMetaData.map((item) => {
        if (item.Equipe === id) {
          return { ...item, expanded: !item?.expanded }
        }
        return item
      })
      dispatch(setGeneralMetaData(newMetaList))
    } else {
      dispatch(
        getGeneralMetaItem(id, {
          AnoMesCota: quota,
          Equipe: id,
          Escritorio: selectedOffice,
          Regiao: null,
        }),
      )
    }
  }

  return (
    <>
      {!loading && !generalMetaData.length ? (
        <EmptyContent
          description="Não foi possível encontrar dados para o período selecionado. Revise as opções selecionadas e tente novamente."
          title="Nenhum resultado encontrado"
        />
      ) : (
        <Table
          bodyData={tableData()}
          customFixedLeftColsWidth={[80, 180, 140, 140]}
          distributeWidth={false}
          fixedLeftCols={[0, 1, 2, 3]}
          hasAsyncExpand
          headData={tableLabels()}
          iconColor={officeColors[selectedOffice]}
          isLoading={loadingTableItem}
          isPrinting={isPrinting}
          rowHeight={48}
          tableRef={screenshotComponentRef}
          toggleExpand={setExpanded}
        />
      )}
    </>
  )
}

export default GeneralTable
